import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Json } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

export function usePersonalAccountData(
  userId: string,
  partialAccount?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
    public_data?: Json;
    clientName?: string | null;
  },
) {
  const client = useSupabase();
  const queryKey = ['account:data', userId];

  const queryFn = async () => {
    if (!userId) {
      return null;
    }

    // Get the user's profile data from the users table
    // This includes their profile picture and name (separate from workspace data)
    const userResponse = await client
      .from('users')
      .select('id, display_name, first_name, last_name, picture_url')
      .eq('id', userId)
      .maybeSingle();

    if (userResponse.error) {
      throw userResponse.error;
    }

    // If no user profile found, return minimal data
    if (!userResponse.data) {
      return {
        id: userId,
        name: null,
        picture_url: null,
        public_data: {},
        display_name: null,
        first_name: null,
        last_name: null,
        clientName: null,
      };
    }

    // Compute clientName: prefer display_name, then first+last name
    let clientName = userResponse.data.display_name ?? null;
    if (!clientName && userResponse.data.first_name && userResponse.data.last_name) {
      clientName = `${userResponse.data.first_name} ${userResponse.data.last_name}`.trim();
    } else if (!clientName && userResponse.data.first_name) {
      clientName = userResponse.data.first_name;
    }

    // Return user profile data
    return {
      id: userResponse.data.id,
      name: clientName,
      picture_url: userResponse.data.picture_url ?? null,
      public_data: {},
      display_name: userResponse.data.display_name ?? null,
      first_name: userResponse.data.first_name ?? null,
      last_name: userResponse.data.last_name ?? null,
      clientName,
    };
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!userId,
    refetchOnWindowFocus: false,
    // Enable refetch on mount to ensure fresh data after page refresh
    // Use 'always' to always refetch, even if initialData is provided
    refetchOnMount: 'always',
    staleTime: 0, // Consider data stale immediately to ensure fresh fetches
    initialData: partialAccount?.id
      ? {
          id: partialAccount.id,
          name: partialAccount.name,
          picture_url: partialAccount.picture_url,
          public_data: partialAccount.public_data ?? {},
          display_name: null,
          first_name: null,
          last_name: null,
          clientName: partialAccount.clientName ?? partialAccount.name,
        }
      : undefined,
  });
}

export function useRevalidatePersonalAccountDataQuery() {
  const queryClient = useQueryClient();

  return useCallback(
    async (userId: string, newPictureUrl?: string | null) => {
      // If we have a new picture URL, optimistically update the cache
      if (newPictureUrl !== undefined) {
        queryClient.setQueryData<ReturnType<typeof usePersonalAccountData>['data']>(['account:data', userId], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            picture_url: newPictureUrl,
          };
        });
      }

      // Invalidate the personal account data query
      await queryClient.invalidateQueries({
        queryKey: ['account:data', userId],
      });

      // Also invalidate any related queries that might cache avatar data
      await queryClient.invalidateQueries({
        queryKey: ['accounts'],
      });

      // Force refetch to ensure immediate update
      await queryClient.refetchQueries({
        queryKey: ['account:data', userId],
        exact: true,
      });
    },
    [queryClient],
  );
}
